import { useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { queryKeys } from '@/lib/queryKeys';
import { FileImportRow } from './FileImportRow';

/**
 * ADR-015 D11 — the multi-file front door. A thin N-instance driver over the
 * existing single-file pipeline (design §10.2): drop 1..N files, and each runs
 * detect → pick → preview → commit INDEPENDENTLY in its own
 * <code>FileImportRow</code>. There is no shared transaction — each file is its
 * own <code>ImportBatch</code> (the unit of undo), so file 3 failing leaves
 * 1, 2, 4, 5 committed and file 3 retryable inline.
 *
 * After any per-file commit settles, the import-history list + sort inbox are
 * invalidated so the new rows appear without a manual reload (design §10.10).
 */
interface QueuedFile {
  id: string;
  file: File;
}

export function MultiFileImportFlow() {
  const [queued, setQueued] = useState<QueuedFile[]>([]);
  const queryClient = useQueryClient();
  const idCounter = useRef(0);

  const addFiles = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const added = Array.from(fileList).map((file) => ({
      id: `f${idCounter.current++}`,
      file,
    }));
    setQueued((prev) => [...prev, ...added]);
  };

  const removeFile = (id: string) =>
    setQueued((prev) => prev.filter((q) => q.id !== id));

  // Design §10.10 — refresh the reframed Sources import-history list + the sort
  // inbox after a per-file commit lands so the §4 "lands in the inbox" guarantee
  // is visibly reflected. React-Query coalesces these, so invalidating once per
  // commit (rather than batching) is cheap and never shows a stale list.
  const refreshAfterCommit = () => {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.finance.importBatches.all,
    });
    void queryClient.invalidateQueries({ queryKey: queryKeys.finance.sortInboxAll() });
  };

  return (
    <div className="space-y-4">
      <MultiDropzone onFiles={addFiles} />

      {queued.length > 0 && (
        <ul className="space-y-3">
          {queued.map(({ id, file }) => (
            <li key={id}>
              <FileImportRow
                file={file}
                onCommitted={refreshAfterCommit}
                onRemove={() => removeFile(id)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MultiDropzone({ onFiles }: { onFiles: (files: FileList | null) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  return (
    <label
      className={cn(
        'flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-8 cursor-pointer text-center text-sm',
        isDragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/30',
      )}
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragOver(false);
        onFiles(e.dataTransfer.files);
      }}
    >
      <Upload className="h-6 w-6 text-muted-foreground" aria-hidden />
      <span className="font-medium text-foreground">
        Drop any files here, or click to browse
      </span>
      <span className="text-xs text-muted-foreground">
        Bank statements, processor exports, or your own donation or expense
        sheets — one or many at once. CSV / Excel / OFX up to 10MB each.
      </span>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        accept=".csv,.xlsx,.xls,.ofx,.qfx"
        onChange={(e) => {
          onFiles(e.target.files);
          // Reset so re-selecting the same file name still fires onChange.
          if (inputRef.current) inputRef.current.value = '';
        }}
      />
    </label>
  );
}
